/**
 * UseCelo Provider - Unified Provider with SSR Support
 *
 * This provider supports both server-side rendering and client-side functionality:
 * - Server Mode: Provides SSR-safe defaults and no-op functions
 * - Client Mode: Full functionality with all sub-providers
 * - Auto Mode: Automatically detects server vs client and renders appropriately
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
// Auth is now handled via simple userId prop
import { UseCeloContextType } from '../types';
import { FeatureAccess, UserPlan, UserSubscription } from '../types';
import { GatewayInfo, CheckoutOptions, CheckoutSession, SubscriptionDetails, PaymentMethod, PortalSessionOptions, SubscriptionCancelOptions, SubscriptionPauseOptions, CurrentSubscriptionData } from '../types/payment';

// ===== TYPES =====

export interface UseCeloConfig {
  // Required config
  appId: string;
  publicKey: string;
  children: ReactNode;

  // API configuration
  apiHost?: string;

  // SSR/Client mode
  mode?: 'auto' | 'ssr' | 'client';

  // Auth configuration - Simple userId approach
  userId?: string | null | (() => string | null | Promise<string | null>);

  // Feature gating behavior
  fallbackBehavior?: 'hide' | 'disable' | 'blur' | 'upgrade';
  cacheTimeout?: number;
  onFeatureBlock?: (featureName: string) => void;
  onUpgradeClick?: (featureName: string) => void;

  // Payment configuration
  enablePayments?: boolean;
  autoLoadGatewayInfo?: boolean;
  onGatewayInfoLoaded?: (info: GatewayInfo) => void;
  onPaymentError?: (error: string) => void;
}

// Re-export types from individual providers for backward compatibility
export type { FeatureAccess, UserPlan, UserSubscription } from '../types';

// Context - export for use in client provider
export const UseCeloContext = createContext<UseCeloContextType | null>(null);

// Server-safe no-op functions
const noop = () => {};
const noopAsync = async () => {};
const noopAsyncReturn = async () => null as any;

// Utility to check if we're on the server
const isServer = typeof window === 'undefined';

/**
 * Server-safe provider that provides default values and no-op functions
 * This prevents SSR hydration mismatches
 */
function UseCeloServerProvider({
  children,
  appId,
  publicKey,
  apiHost = "https://usecelo.com",
  enablePayments = false,
}: Omit<UseCeloConfig, 'userId' | 'children' | 'mode'> & { children: ReactNode }) {

  // Server-safe context value with defaults
  const contextValue: UseCeloContextType = {
    // Core state (safe defaults)
    features: [],
    loading: false, // Don't show loading on server to prevent layout shift
    error: null,
    userPlan: null,
    subscription: null,

    // Auth (server-safe default)
    userId: null,

    // Feature methods (conservative server defaults)
    checkAccess: () => false, // Conservative: deny access on server
    getFeature: () => undefined,
    hasFeature: () => false,

    // Usage tracking (no-ops)
    trackUsage: noopAsync,
    trackSingleUsage: noopAsync,

    // Data management (no-ops)
    refreshFeatures: noopAsync,

    // Configuration
    appId,
    publicKey,
    apiHost,

    // Payment integration (undefined - not available on server)
    ...(enablePayments && {
      paymentClient: undefined,
      gatewayInfo: null,
      isPaymentConfigured: false,
      createCheckout: noopAsyncReturn,
      getCheckoutUrl: noopAsyncReturn,
      createPortalSession: noopAsyncReturn,
      getSubscription: noopAsyncReturn,
      cancelSubscription: noopAsync,
      pauseSubscription: noopAsync,
      resumeSubscription: noopAsync,
      getPaymentMethods: noopAsyncReturn,
      refreshGatewayInfo: noopAsync,
    })
  };

  return (
    <UseCeloContext.Provider value={contextValue}>
      {children}
    </UseCeloContext.Provider>
  );
}

/**
 * Client-side provider wrapper
 */
function UseCeloClientProvider(props: Omit<UseCeloConfig, 'mode'>) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // During hydration, show server provider to prevent mismatch
  if (!mounted) {
    return <UseCeloServerProvider {...props} />;
  }

  // After hydration, import and render client provider
  const { UseCeloClientProvider: ClientProvider } = require('./UseCeloClientProvider');
  return <ClientProvider {...props} />;
}

/**
 * Main provider component with conditional rendering
 */
export function UseCeloProvider({
  userId,
  mode = 'auto',
  ...props
}: UseCeloConfig) {
  // Handle SSR mode
  if (mode === 'ssr' || (mode === 'auto' && isServer)) {
    return <UseCeloServerProvider {...props}>{props.children}</UseCeloServerProvider>;
  }

  // Client-side: use client provider directly (no AuthProvider needed)
  return <UseCeloClientProvider userId={userId} {...props} />;
}

// ===== HOOKS =====

/**
 * Hook to access the UseCelo context (works in both server and client)
 */
export function useUseCelo(): UseCeloContextType {
  const context = useContext(UseCeloContext);
  if (!context) {
    // During SSR/hydration, provide safe defaults instead of throwing
    if (typeof window === 'undefined') {
      return {
        // Core state (safe defaults)
        features: [],
        loading: false,
        error: null,
        userPlan: null,
        subscription: null,

        // Auth (server-safe default)
        userId: null,

        // Feature methods (conservative server defaults)
        checkAccess: () => false,
        getFeature: () => undefined,
        hasFeature: () => false,

        // Usage tracking (no-ops)
        trackUsage: async () => {},
        trackSingleUsage: async () => {},

        // Data management (no-ops)
        refreshFeatures: async () => {},

        // Configuration (defaults)
        appId: '',
        publicKey: '',
        apiHost: 'https://usecelo.com',

        // Payment integration (undefined - not available on server)
        paymentClient: undefined,
        gatewayInfo: null,
        isPaymentConfigured: false,
        createCheckout: async () => ({ url: '', sessionId: '', gateway: 'stripe' as any }),
        getCheckoutUrl: async () => '',
        createPortalSession: async () => ({ url: '' }),
        getSubscription: async () => null,
        cancelSubscription: async () => {},
        pauseSubscription: async () => {},
        resumeSubscription: async () => {},
        getPaymentMethods: async () => [],
        refreshGatewayInfo: async () => {},
      };
    }
    throw new Error('useUseCelo must be used within UseCeloProvider');
  }
  return context;
}

/**
 * Hook for feature access (server-safe)
 */
export function useFeature(featureTag: string) {
  const { getFeature, hasFeature, loading } = useUseCelo();
  const feature = getFeature(featureTag);

  return {
    hasAccess: hasFeature(featureTag),
    feature,
    config: feature?.config,
    limits: feature?.limits,
    usage: feature?.usage,
    loading,
  };
}

/**
 * Feature component for declarative access control (server-safe)
 */
export function Feature({
  name,
  children,
  fallback,
}: {
  name: string;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { hasFeature } = useUseCelo();
  return hasFeature(name) ? <>{children}</> : <>{fallback}</>;
}

// ===== PAYMENT HOOKS =====

/**
 * Hook for checkout operations (when payments enabled)
 */
export function useCheckout() {
  const context = useUseCelo();

  if (!context.createCheckout || !context.getCheckoutUrl) {
    throw new Error('Payments not enabled. Set enablePayments: true in UseCeloProvider');
  }

  return {
    createCheckout: context.createCheckout,
    getCheckoutUrl: context.getCheckoutUrl,
    initiateCheckout: async (planId: string, options?: Partial<CheckoutOptions>): Promise<void> => {
      if (isServer) return; // No-op on server
      const url = await context.getCheckoutUrl!(planId, options);
      window.location.href = url;
    },
    isReady: context.isPaymentConfigured || false,
    gateway: context.gatewayInfo?.gateway || null,
  };
}

/**
 * Hook for subscription management (when payments enabled)
 */
export function useSubscriptionManagement() {
  const context = useUseCelo();

  if (!context.getSubscription) {
    throw new Error('Payments not enabled. Set enablePayments: true in UseCeloProvider');
  }

  const [subscriptionDetails, setSubscriptionDetails] = useState<CurrentSubscriptionData | null>(null);
  const [subLoading, setSubLoading] = useState(false);

  const loadSubscription = async (userId?: string) => {
    if (isServer) return null; // No-op on server
    setSubLoading(true);
    try {
      const sub = await context.getSubscription!(userId);
      setSubscriptionDetails(sub);
      return sub;
    } finally {
      setSubLoading(false);
    }
  };

  return {
    subscription: subscriptionDetails,
    isLoading: subLoading,
    loadSubscription,
    cancelSubscription: context.cancelSubscription!,
    pauseSubscription: context.pauseSubscription!,
    resumeSubscription: context.resumeSubscription!,
    getPaymentMethods: context.getPaymentMethods!,
  };
}